import { jsTypeHelper } from '@tap/utils';
import React from 'react';

export type UseFetchDataState<T> = {
    data: T | undefined,
    /*
    isLoading Используется во многих местах не правильно. И при перезагрузке данных (обновлении) cтраница может мелькать.
    Для отображения лоадеров при первой загрузке страницы нужно использовать свойство isReady.
    Для быстрого решения, сейчас isLoading при обновлении данных не устанавливается. Чтобы изменить это поведение есть флаг updateIsLoading
    По идее сейчас дефолтное поведение совпадает со свойством isReady. Постепенно нужно перейти на него, а isLoading устанавливать при люой загрузке данных
    Сейчас нормальное поведение isLoading  реализовано в свойстве trueIsLoading
    */
    isLoading: boolean,
    isReady: boolean,
    // Временная замена isLoading
    trueIsLoading: boolean,
    error: any,
}

export type FetchProps<P> = {
    requestProps?: P,
    updateIsLoading?: boolean,
}

export type UseFetchDataFullState<T, P = any> = UseFetchDataState<T> & {
    fetchData: (props?: FetchProps<P>) => Promise<T>,
    setData: (data: T | undefined | ((currentData: T | undefined) => T | undefined)) => void,
    setIsLoading: (isLoading: boolean) => void,
    setError: (error: any) => void,
}

export type UseFetchDataResult<T, P> = {
    0: UseFetchDataFullState<T, P>,
    1: T | undefined,
    2: boolean,
    3: any,
} & Array<UseFetchDataFullState<T, P> | T | boolean | any>

export type UseFetchDataParams<T> = {
    autoFetch: boolean,
    defaultValue: T,
    mergePages: (data: T | undefined, newPage: T) => T,
}

const defaultParams = {
    autoFetch: true,
};

export const useFetchData = <T, P>(
    fetchDataRequest: (requestProps?: P) => Promise<T>,
    deps: React.DependencyList,
    customParams: Partial<UseFetchDataParams<T>> = {},
): UseFetchDataResult<T, P> => {
    const { autoFetch, defaultValue } = { ...defaultParams, ...customParams };

    const [state, setState] = React.useState<UseFetchDataState<T>>({
        data: defaultValue,
        isLoading: autoFetch,
        trueIsLoading: autoFetch,
        isReady: false,
        error: null,
    });

    const fetchData = React.useCallback(async (props?: FetchProps<P>) => {
        if (props?.updateIsLoading) {
            setState((prev) => ({ ...prev, trueIsLoading: true, isLoading: true }));
        } else {
            setState((prev) => ({ ...prev, trueIsLoading: true }));
        }

        try {
            const res = await fetchDataRequest(props?.requestProps);
            setState((prev) => ({
                ...prev,
                isLoading: false,
                trueIsLoading: false,
                data: customParams.mergePages ? customParams.mergePages(prev.data, res) : res,
                isReady: true,
            }));
            return res;
        } catch (error) {
            setState((prev) => ({
                ...prev,
                isLoading: false,
                trueIsLoading: false,
                error: error,
            }));
            throw error;
        }
    }, [...deps]);

    const setData = React.useCallback((value: T | undefined | ((currentValue: T | undefined) => T | undefined)) => {
        if (jsTypeHelper.isFunction(value)) {
            setState((prev) => ({ ...prev, data: value(prev.data) }));
        } else {
            setState((prev) => ({ ...prev, data: value }));
        }
    }, [...deps]);

    const setIsLoading = React.useCallback((isLoading: boolean) => {
        setState((prev) => ({ ...prev, isLoading, trueIsLoading: isLoading }));
    }, [...deps]);

    const setError = React.useCallback((error: any) => {
        setState((prev) => ({ ...prev, error }));
    }, [...deps]);

    React.useEffect(() => {
        setState({
            data: defaultValue,
            isLoading: autoFetch,
            trueIsLoading: autoFetch,
            isReady: false,
            error: null,
        });

        if (autoFetch) {
            fetchData();
        }
    }, [...deps, fetchData, autoFetch]);

    return [{ ...state, fetchData, setData, setIsLoading, setError }, state.data, state.isLoading, state.error];
};